import React, { FormEventHandler } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router'
import dayjs from 'dayjs'

import { AccountHeader } from '@mobi/component-library/Account'
import {
  Button,
  InfoBox,
  CustomerServiceEmail,
  Input,
  Checkbox,
} from '@mobi/component-library/Common/V2'
import { spacing } from '@mobi/component-library/Theme/Common'
import {
  getFormattedDateOfBirth,
  validateDateOfBirth,
} from '@mobi/component-library/Verification/helpers/dateOfBirth'
import { unwrapErrorMessage, serviceCenterEmail } from '@mobi/utils'
import { NameInput, AddressInput, Nationality, CountrySelectField } from '@mobi/account/Common'
import { AccountHolderAddress } from '@mobi/api-types'
import {
  getEmailValidationMessage,
  getMobileValidationMessage,
  isEmailValid,
  isPhoneNumberValid,
  isValidName,
  trackSignUpAlreadyHaveAccount,
  trackSignUpError,
  trackSignUpJoin,
  trackSignUpStart,
  isVerificationSuccessful,
  routes,
  ACCOUNT_VERIFICATION_STATUS_OPTIMOVE_EVENT,
} from '../Utils'
import {
  getSignUpState,
  setHasUserStartedSignup,
  setNumberOfAttemptsToVerify,
  setSignupApplication,
  setSignupVerification,
  setVerifiedFrom,
} from '../Store'
import {
  useSignUpApplication,
  type TypeSignUpApplicationRequest,
  type TypeSignUpApplicationResponse,
} from '../Hooks'
import {
  SignupError,
  BoldStyled,
  Caption,
  CheckboxLabel,
  FooterStyled,
  FormSection,
  Legend,
  PageLayout,
  PersonalDetailsDisclaimer,
} from './'
import { Contract } from '@mobi/web-native-comms'
import { trackVerificationSuccess } from '../../Verification/Utils/ga'

const AFFILIATE_ID = 'affiliateid'
const VENUE_ID = 'venueid'
const LOCATION_ID = 'locationid'

type EntryMode = 'Auto' | 'Manual'

interface PersonalDetailsProps {
  trackOptimoveEvent: (event: Contract['OPTIMOVE_TRACK']) => void
}

export const PersonalDetails = ({ trackOptimoveEvent }: PersonalDetailsProps) => {
  const navigate = useNavigate()
  const [showErrorPopup, setShowErrorPopup] = React.useState(false)
  const state = useSelector(getSignUpState)
  const dispatch = useDispatch()

  const initialDateOfBirth = state?.signupApplication?.birthdate
    ? dayjs(state.signupApplication.birthdate).format('DD/MM/YYYY')
    : ''
  const [dateOfBirth, setDateOfBirth] = React.useState(initialDateOfBirth)
  const [firstName, setFirstName] = React.useState(state?.signupApplication?.firstName || '')
  const [middleName, setMiddleName] = React.useState(state?.signupApplication?.middleName || '')
  const [surname, setSurname] = React.useState(state?.signupApplication?.surname || '')
  const [emailAddress, setEmailAddress] = React.useState(state?.signupApplication?.email || '')
  const [mobile, setMobile] = React.useState(state?.signupApplication?.mobileNumber || '')
  const [showPreviousAddress, setShowPreviousAddress] = React.useState(false)
  const [currentCountry, setCurrentCountry] = React.useState(Nationality.Australia)
  const [previousCountry, setPreviousCountry] = React.useState(Nationality.Australia)
  const [currentAddress, setCurrentAddress] = React.useState<AccountHolderAddress | undefined>(
    undefined
  )
  const [previousAddress, setPreviousAddress] = React.useState<AccountHolderAddress | undefined>(
    undefined
  )
  const isValidAddress =
    !!currentAddress &&
    (!showPreviousAddress || !!previousAddress || previousCountry === Nationality.International)
  const [hasAgreedToMarketing, setHasAgreedToMarketing] = React.useState(
    state?.signupApplication?.marketingOptInIndicator || false
  )
  const [focusedField, setFocusedField] = React.useState<string | null>(null)
  const [validationMessages, setValidationMessages] = React.useState<Record<string, string>>({})
  const hasTrackedSignupStart = React.useRef(false)
  const { saveSignUpApplication, saveSignUpApplicationStatus } = useSignUpApplication()
  const [entryMode, setEntryMode] = React.useState<EntryMode>('Auto')
  // #region helper functions
  function useQuery() {
    const params = new URLSearchParams()
    new URLSearchParams(useLocation().search).forEach((value, name) => {
      params.append(name.toLowerCase(), value)
    })
    return params
  }

  const queries = useQuery()

  const buildAddress = (address: AccountHolderAddress | undefined, country: string) => ({
    country: country,
    unitNumber: address?.unitNumber,
    streetNumber: address?.streetNumber,
    streetName: address?.streetName,
    streetType: address?.streetType,
    postCode: address?.postcode,
    state: address?.state,
    suburb: address?.suburb,
  })

  function getSignupApplication(): TypeSignUpApplicationRequest {
    const dto = {
      firstName,
      middleName,
      surname,
      birthdate: dayjs(dateOfBirth, 'DD/MM/YYYY').format('YYYY-MM-DD'),
      email: emailAddress,
      mobileNumber: mobile,
      applicationReferenceNumber: state?.signupVerification?.applicationReferenceNumber,
      enquiryId: state?.signupVerification?.enquiryId,
      residentialAddress: buildAddress(currentAddress, currentCountry),
      previousAddress:
        previousAddress || previousCountry === Nationality.International
          ? buildAddress(previousAddress, previousCountry)
          : undefined,
      marketingOptInIndicator: hasAgreedToMarketing,
      affiliateMarketingId: queries.get(AFFILIATE_ID) as string,
      locationId: queries.get(LOCATION_ID) as string,
      venueId: queries.get(VENUE_ID) as string,
    }
    return dto
  }

  function getFormattedMobile(enteredText: string) {
    return enteredText.replace(/[\D]/gi, '')
  }

  const onSubmit: FormEventHandler = e => {
    e.preventDefault()
    dispatch(setNumberOfAttemptsToVerify())
    const application = getSignupApplication()
    saveSignUpApplication(application, {
      onSuccess: verification => {
        setSignupApplicationAndVerification(
          {
            ...application,
            applicationReferenceNumber: verification.applicationReferenceNumber,
            enquiryId: verification.enquiryId,
          },
          verification
        )
      },
      onError: error => {
        setShowErrorPopup(true)
        trackSignUpError(location.pathname, unwrapErrorMessage(error))
      },
    })
  }

  const setSignupApplicationAndVerification = (
    application: TypeSignUpApplicationRequest,
    verification: TypeSignUpApplicationResponse
  ) => {
    const isSuccessful = isVerificationSuccessful(verification)

    dispatch(setSignupVerification(verification))
    dispatch(setSignupApplication(application))

    if (isSuccessful) {
      dispatch(setVerifiedFrom('verified-address'))
      trackVerificationSuccess('Verified Address')
      trackOptimoveEvent(ACCOUNT_VERIFICATION_STATUS_OPTIMOVE_EVENT)
      trackSignUpJoin(entryMode, application.marketingOptInIndicator)
      navigate(routes.password)
      return
    }

    if (verification.duplicateExists || verification.potentialSanctionedMatch) {
      navigate(routes.signUpUnsuccessful)
      return
    }

    if (!verification.isVerified) {
      trackSignUpError(location.pathname, 'user is not verified')
      navigate(routes.verificationOptions)
      return
    }
  }
  // #endregion

  const isOver18 = !!dateOfBirth && !validationMessages.dateOfBirth
  const hasValidEntries = Object.values(validationMessages).every(error => error === '')
  const isComplete =
    hasValidEntries &&
    dateOfBirth.length > 0 &&
    isValidName(firstName) &&
    isValidName(middleName, true) &&
    isValidName(surname) &&
    isEmailValid(emailAddress) &&
    isPhoneNumberValid(mobile) &&
    isValidAddress
  const isBusy = saveSignUpApplicationStatus === 'pending'

  const resetFieldValidation = (field: keyof typeof validationMessages) =>
    setValidationMessages(prev => ({
      ...prev,
      [field]: '',
    }))

  return (
    <form name='contact-details-form' onSubmit={onSubmit}>
      <PageLayout>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Button
            color='secondary_grey'
            onClick={() => {
              trackSignUpAlreadyHaveAccount(state?.hasUserStartedSignup ?? false)
              navigate('/account')
            }}
            style={{ marginTop: spacing.md }}
            disabled={isBusy}
            type='button'
          >
            <span>
              Already have a <BoldStyled>TABtouch account?</BoldStyled>
            </span>
          </Button>
        </div>
        <AccountHeader
          alignItems='start'
          title='Join now and start betting!'
          description={
            <InfoBox color='blue' size='sm' hideIcon={true}>
              Enter your full name and DOB as it appears on your ID documents (including middle name
              if applicable)
            </InfoBox>
          }
          style={{ marginBottom: 0 }}
        />
        <FormSection>
          <Legend>My details</Legend>
          <Input
            label='Date of Birth'
            name='dateOfBirth'
            placeholder='DD / MM / YYYY'
            required
            maxLength={10}
            value={dateOfBirth}
            onChange={e =>
              setDateOfBirth(
                getFormattedDateOfBirth(e.target.value, dateOfBirth, e.target.selectionStart)
              )
            }
            onFocus={() => {
              resetFieldValidation('dateOfBirth')
              if (!hasTrackedSignupStart.current) {
                trackSignUpStart()
                dispatch(setHasUserStartedSignup(true))
                hasTrackedSignupStart.current = true
              }
              setFocusedField('dateOfBirth')
            }}
            onBlur={e => {
              if (focusedField === 'dateOfBirth') setFocusedField(null)
              setValidationMessages(prev => ({
                ...prev,
                dateOfBirth: validateDateOfBirth(
                  getFormattedDateOfBirth(e.target.value, dateOfBirth, e.target.selectionStart)
                ),
              }))
            }}
            isInvalid={!!validationMessages.dateOfBirth}
            helpText={
              validationMessages.dateOfBirth || 'You must be aged 18 or over to wager in Australia'
            }
            isDisabled={isBusy}
          />
          {focusedField === 'dateOfBirth' && (
            <InfoBox color='blue' size='sm'>
              Your DOB must match your ID documents
            </InfoBox>
          )}

          <NameInput
            firstName={firstName}
            middleName={middleName}
            lastName={surname}
            setFirstName={setFirstName}
            setMiddleName={setMiddleName}
            setLastName={setSurname}
            isDisabled={isBusy || !isOver18}
          />

          <Input
            type='tel'
            label='Mobile'
            name='mobile'
            placeholder='Enter a valid mobile number'
            required
            maxLength={10}
            value={mobile}
            onChange={e => setMobile(getFormattedMobile(e.target.value))}
            onFocus={() => resetFieldValidation('mobile')}
            onBlur={e =>
              setValidationMessages(prev => ({
                ...prev,
                mobile: getMobileValidationMessage(e.target.value),
              }))
            }
            isInvalid={!!validationMessages.mobile}
            helpText={validationMessages.mobile}
            isDisabled={isBusy || !isOver18}
          />

          <Input
            type='email'
            label='Email'
            name='email'
            placeholder='Enter a valid email address'
            required
            maxLength={50}
            value={emailAddress}
            onChange={e => setEmailAddress(e.target.value)}
            onFocus={() => resetFieldValidation('email')}
            onBlur={e =>
              setValidationMessages(prev => ({
                ...prev,
                email: getEmailValidationMessage(e.target.value),
              }))
            }
            isInvalid={!!validationMessages.email}
            helpText={validationMessages.email}
            isDisabled={isBusy || !isOver18}
          />

          <div>
            <Checkbox
              id='sign-up-marketing-subscription'
              name='MarketingOptIn'
              checked={hasAgreedToMarketing || false}
              label={
                <CheckboxLabel>
                  I consent to receive advertising and promotional material by email, post, SMS,
                  telephone and other direct means in line with TABtouch’s{' '}
                  <a
                    href='https://www.rwwa.com.au/privacy-policy/'
                    rel='noopener noreferrer'
                    target='_blank'
                  >
                    Privacy Policy
                  </a>
                </CheckboxLabel>
              }
              data-tid-sign-up-marketing-subscription=''
              onChange={e => {
                setHasAgreedToMarketing(e.target.checked)
              }}
              isDisabled={isBusy || !isOver18}
            />
          </div>
        </FormSection>
        <FormSection>
          <Legend>
            <div>Current Address</div>
          </Legend>
          <CountrySelectField
            value={currentCountry}
            onChange={value => {
              setCurrentCountry(value)
              setCurrentAddress(undefined)
            }}
            isDisabled={isBusy || !isOver18}
          />
          {currentCountry === Nationality.Australia && (
            <>
              <AddressInput
                prefix='current-address'
                onValidationChange={change => {
                  if (change.isValid) setCurrentAddress(change.result)
                  else setCurrentAddress(undefined)
                }}
                onEntryModeChanged={mode => setEntryMode(mode === 'Manual' ? 'Manual' : 'Auto')}
                isDisabled={isBusy || !isOver18}
              />
              <Checkbox
                id='sign-up-2-years'
                label={
                  <CheckboxLabel>Have you lived at this address less than 2 years?</CheckboxLabel>
                }
                name='TwoYears'
                checked={showPreviousAddress}
                data-tid-sign-up-has-been-2-years=''
                onChange={e => {
                  setShowPreviousAddress(e.target.checked)
                  setPreviousAddress(undefined)
                  setPreviousCountry(Nationality.Australia)
                }}
                isDisabled={isBusy || !isOver18}
              />
              <Caption>
                Only a physical address can be used for verification purposes. You can change your
                postal address (including PO box addresses) once your account has been opened.
              </Caption>
              {showPreviousAddress && (
                <>
                  <Legend>
                    <div>Previous Address</div>
                  </Legend>
                  <CountrySelectField
                    value={previousCountry}
                    onChange={value => {
                      setPreviousCountry(value)
                      setPreviousAddress(undefined)
                    }}
                    isDisabled={isBusy || !isOver18}
                  />
                  {previousCountry !== Nationality.International && (
                    <AddressInput
                      prefix='previous-address'
                      onValidationChange={change => {
                        if (change.isValid) setPreviousAddress(change.result)
                        else setPreviousAddress(undefined)
                      }}
                      isDisabled={isBusy || !isOver18}
                    />
                  )}
                </>
              )}
            </>
          )}
          {currentCountry !== Nationality.Australia && (
            <InfoBox color='grey' size='md'>
              <span>Don't have an Australian address?</span>
              <div>
                Our account opening process is a little different if you're living outside
                Australia. Please email us at <CustomerServiceEmail /> to assist you.
              </div>
            </InfoBox>
          )}
        </FormSection>
        <FooterStyled>
          {currentCountry === Nationality.Australia && (
            <>
              <Button
                type='submit'
                isFullWidth
                disabled={!isComplete || !isOver18}
                isLoading={isBusy}
              >
                Next
              </Button>
              <InfoBox color='grey' size='md' hideIcon={true}>
                <PersonalDetailsDisclaimer />
              </InfoBox>
            </>
          )}
          {currentCountry !== Nationality.Australia && (
            <Button
              type='button'
              isFullWidth
              onClick={() => {
                navigate(`mailto:${serviceCenterEmail}`)
              }}
            >
              Email now
            </Button>
          )}
        </FooterStyled>
      </PageLayout>
      <SignupError isOpen={showErrorPopup} onClose={() => setShowErrorPopup(false)} />
    </form>
  )
}
